<template>
    <div class="helpv2" v-click-outside="close">
        <transition name="helpv2-fade">
            <div v-if="open" class="helpv2-panel" role="dialog" aria-label="Aide">
                <header class="helpv2-panel-header">
                    <i class="fal fa-headset helpv2-panel-icon"></i>
                    <div class="helpv2-panel-titles">
                        <strong class="helpv2-panel-title">{{ title }}</strong>
                        <span class="helpv2-panel-subtitle">
                            {{ pageTips.length ? 'Tips sur cette page + rubriques générales' : subtitle }}
                        </span>
                    </div>
                    <button
                        type="button"
                        class="helpv2-close"
                        aria-label="Fermer"
                        @click="close"
                    >
                        <i class="fal fa-times"></i>
                    </button>
                </header>

                <section v-if="pageTips.length" class="helpv2-context">
                    <span class="helpv2-context-label">
                        <i class="fal fa-sparkles"></i>
                        Sur cette page
                    </span>
                    <div
                        v-for="(tip, index) in pageTips"
                        :key="index"
                        class="helpv2-tip"
                    >
                        <span class="helpv2-tip-icon">
                            <i :class="tip.icon"></i>
                        </span>
                        <div class="helpv2-tip-body">
                            <strong class="helpv2-tip-title">{{ tip.title }}</strong>
                            <span class="helpv2-tip-desc">{{ tip.description }}</span>
                        </div>
                    </div>
                </section>

                <div class="helpv2-items">
                    <router-link
                        to="/documentation"
                        class="helpv2-item"
                        @click.native="close"
                    >
                        <span class="helpv2-item-icon helpv2-item-icon--docs">
                            <i class="fad fa-question-square"></i>
                        </span>
                        <span class="helpv2-item-body">
                            <span class="helpv2-item-title">Documentation</span>
                            <span class="helpv2-item-desc">Guide d'utilisation de la plateforme</span>
                        </span>
                        <i class="fal fa-arrow-right helpv2-item-arrow"></i>
                    </router-link>

                    <a
                        href="https://support.bouge.io"
                        target="_blank"
                        rel="noopener"
                        class="helpv2-item"
                        @click="close"
                    >
                        <span class="helpv2-item-icon helpv2-item-icon--support">
                            <i class="fad fa-exclamation-triangle"></i>
                        </span>
                        <span class="helpv2-item-body">
                            <span class="helpv2-item-title">Signaler un incident</span>
                            <span class="helpv2-item-desc">support.bouge.io</span>
                        </span>
                        <i class="fal fa-external-link helpv2-item-arrow"></i>
                    </a>

                    <a
                        href="https://www.bougemaville.com/mentions-legales/"
                        target="_blank"
                        rel="noopener"
                        class="helpv2-item"
                        @click="close"
                    >
                        <span class="helpv2-item-icon helpv2-item-icon--legal">
                            <i class="fad fa-info-circle"></i>
                        </span>
                        <span class="helpv2-item-body">
                            <span class="helpv2-item-title">Mentions légales</span>
                            <span class="helpv2-item-desc">Conditions et confidentialité</span>
                        </span>
                        <i class="fal fa-external-link helpv2-item-arrow"></i>
                    </a>
                </div>

                <a
                    href="https://coeurduweb.com/"
                    target="_blank"
                    rel="noopener"
                    class="helpv2-footer"
                    @click="close"
                >
                    <i class="fas fa-heart helpv2-footer-heart"></i>
                    <span>Propulsé par <strong>Coeur du Web</strong></span>
                </a>

            </div>
        </transition>

        <button
            type="button"
            class="helpv2-fab"
            :class="{
                'helpv2-fab--open': open,
                'helpv2-fab--attention': attention
            }"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-label="open ? 'Fermer l\'aide' : 'Ouvrir l\'aide'"
            @click="toggle"
        >
            <i class="fal" :class="open ? 'fa-times' : 'fa-headset'"></i>
        </button>
    </div>
</template>

<script>
import helpConfig from '@/config/help'

export default {
    name: 'HelpWidget',
    props: {
        title: {
            type: String,
            default: 'Besoin d\'aide ?'
        },
        subtitle: {
            type: String,
            default: 'Choisissez une rubrique'
        }
    },
    computed: {
        pageTips() {
            const routeName = this.$route && this.$route.name
            if (!routeName) return []
            return helpConfig[routeName] || []
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (e) => {
                    if (!el.contains(e.target)) binding.value()
                }
                document.addEventListener('mousedown', el._clickOutside)
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._clickOutside)
            }
        }
    },
    data() {
        return {
            open: false,
            attention: false,
            attentionTimer: null,
            attentionDelayTimer: null,
            notifiedRoutes: new Set()
        }
    },
    watch: {
        '$route.name': {
            immediate: true,
            handler(name) {
                this.scheduleAttention(name)
            }
        }
    },
    mounted() {
        document.addEventListener('keydown', this.onKey)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.onKey)
        this.clearAttentionTimers()
    },
    methods: {
        toggle() {
            this.open = !this.open
            if (this.open) {
                this.attention = false
                this.clearAttentionTimers()
            }
        },
        close() {
            this.open = false
        },
        onKey(e) {
            if (e.key === 'Escape' && this.open) this.close()
        },
        clearAttentionTimers() {
            if (this.attentionTimer) {
                clearTimeout(this.attentionTimer)
                this.attentionTimer = null
            }
            if (this.attentionDelayTimer) {
                clearTimeout(this.attentionDelayTimer)
                this.attentionDelayTimer = null
            }
        },
        scheduleAttention(routeName) {
            // Reset any pending trigger from a previous route so a fast
            // back-to-back navigation doesn't fire the animation of A
            // while B is rendering.
            this.clearAttentionTimers()
            if (!routeName) return
            if (this.open) return
            if (this.notifiedRoutes.has(routeName)) return

            // Delay a bit so the new page's first paint has time to
            // settle — otherwise the shake fires before the page is
            // visible.
            this.attentionDelayTimer = setTimeout(() => {
                this.attentionDelayTimer = null
                this.triggerAttention(routeName)
            }, 600)
        },
        triggerAttention(routeName) {
            if (this.open) return
            if (this.pageTips.length === 0) return
            if (this.notifiedRoutes.has(routeName)) return
            if (this.prefersReducedMotion()) {
                this.notifiedRoutes.add(routeName)
                return
            }

            this.notifiedRoutes.add(routeName)
            this.attention = true
            this.attentionTimer = setTimeout(() => {
                this.attention = false
                this.attentionTimer = null
            }, 2000)
        },
        prefersReducedMotion() {
            if (typeof window === 'undefined' || !window.matchMedia) return false
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        }
    }
}
</script>

<style scoped>
.helpv2 {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9500;
    font-family: var(--v2-font-sans);
}

/* FAB */
.helpv2-fab {
    position: relative;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: none;
    background: var(--v2-primary);
    color: var(--v2-primary-contrast);
    cursor: pointer;
    box-shadow:
        0 10px 28px rgba(8, 145, 178, 0.4),
        0 4px 10px rgba(15, 23, 42, 0.12);
    font-size: 1.15rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition:
        background var(--v2-transition-fast),
        box-shadow var(--v2-transition-base),
        transform var(--v2-transition-base);
    box-sizing: border-box !important;
    -webkit-appearance: none !important;
    appearance: none !important;
    outline: none;
    animation: helpv2-float 3.6s ease-in-out infinite;
}

/* Halo ring used for the attention pulse — hidden by default */
.helpv2-fab::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid var(--v2-primary);
    opacity: 0;
    pointer-events: none;
}

.helpv2-fab:hover {
    background: var(--v2-primary-hover);
    box-shadow:
        0 14px 32px rgba(8, 145, 178, 0.5),
        0 6px 14px rgba(15, 23, 42, 0.15);
    transform: translateY(-3px);
    animation-play-state: paused;
}

.helpv2-fab:active {
    transform: translateY(-1px);
}

.helpv2-fab:focus-visible {
    outline: none;
    box-shadow:
        var(--v2-shadow-focus),
        0 10px 28px rgba(8, 145, 178, 0.4);
}

.helpv2-fab i {
    color: #fff !important;
    transition: transform var(--v2-transition-base);
}

.helpv2-fab--open {
    animation-play-state: paused;
}

.helpv2-fab--open i {
    transform: rotate(90deg);
}

@keyframes helpv2-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
}

/* Attention pulse — triggered once on landing on a page with contextual
 * help. The shake overrides the idle float for its duration, and the
 * halo ring fires via the ::after pseudo-element. */
.helpv2-fab--attention {
    animation: helpv2-attention 2s ease-in-out 1;
}

.helpv2-fab--attention::after {
    animation: helpv2-halo 2s ease-out 1;
}

@keyframes helpv2-attention {
    0% { transform: translateY(0) rotate(0); }
    12% { transform: translateY(-6px) rotate(-8deg) scale(1.08); }
    24% { transform: translateY(-2px) rotate(8deg) scale(1.05); }
    36% { transform: translateY(-6px) rotate(-6deg) scale(1.08); }
    48% { transform: translateY(-2px) rotate(4deg) scale(1.04); }
    60% { transform: translateY(-4px) rotate(-2deg) scale(1.02); }
    100% { transform: translateY(0) rotate(0) scale(1); }
}

@keyframes helpv2-halo {
    0% {
        opacity: 0.75;
        transform: scale(1);
    }
    60% {
        opacity: 0.3;
    }
    100% {
        opacity: 0;
        transform: scale(1.7);
    }
}

@media (prefers-reduced-motion: reduce) {
    .helpv2-fab,
    .helpv2-fab--attention,
    .helpv2-fab--attention::after {
        animation: none;
    }
}

/* Panel */
.helpv2-panel {
    position: absolute;
    bottom: calc(100% + 14px);
    right: 0;
    width: 340px;
    max-width: calc(100vw - 40px);
    max-height: calc(100vh - 120px);
    display: flex;
    flex-direction: column;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-xl, 16px);
    box-shadow:
        0 20px 40px rgba(15, 23, 42, 0.14),
        0 6px 14px rgba(15, 23, 42, 0.08);
    overflow: hidden;
    transform-origin: bottom right;
}

.helpv2-panel-header {
    flex-shrink: 0;
}

.helpv2-context,
.helpv2-items {
    overflow-y: auto;
}

.helpv2-context {
    flex-shrink: 1;
}

.helpv2-items {
    flex-shrink: 0;
    border-top: 1px solid var(--v2-border-subtle);
}

.helpv2-panel-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 14px 12px;
    border-bottom: 1px solid var(--v2-border-subtle);
    background: var(--v2-primary-soft);
}

.helpv2-panel-icon {
    font-size: 1.1rem;
    color: var(--v2-primary);
    margin-top: 2px;
}

.helpv2-panel-titles {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.helpv2-panel-title {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.helpv2-panel-subtitle {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.helpv2-close {
    width: 24px;
    height: 24px;
    border-radius: var(--v2-radius-sm);
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
    font-size: 0.8rem;
    flex-shrink: 0;
}

.helpv2-close:hover {
    background: rgba(8, 145, 178, 0.12);
    color: var(--v2-primary);
}

.helpv2-context {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px 10px;
    background: linear-gradient(
        180deg,
        var(--v2-primary-soft, rgba(8, 145, 178, 0.08)) 0%,
        transparent 100%
    );
    border-bottom: 1px solid var(--v2-border-subtle);
}

.helpv2-context-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--v2-primary);
}

.helpv2-context-label i {
    font-size: 0.7rem;
}

.helpv2-tip {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 8px 10px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    box-shadow: var(--v2-shadow-sm);
}

.helpv2-tip-icon {
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.85rem;
}

.helpv2-tip-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.helpv2-tip-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--v2-text-1);
    line-height: 1.3;
}

.helpv2-tip-desc {
    font-size: 0.76rem;
    color: var(--v2-text-3);
    line-height: 1.45;
}

.helpv2-items {
    display: flex;
    flex-direction: column;
    padding: 6px;
}

.helpv2-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px;
    border-radius: var(--v2-radius-md);
    color: var(--v2-text-2);
    text-decoration: none;
    background: transparent;
    border: none;
    font-family: var(--v2-font-sans);
    font-size: var(--v2-text-sm-size);
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.helpv2-item:hover {
    background: var(--v2-surface-2);
}

.helpv2-item-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--v2-radius-md);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.95rem;
}

.helpv2-item-icon i {
    color: #fff !important;
}

.helpv2-item-icon--docs {
    background: var(--v2-primary);
}

.helpv2-item-icon--support {
    background: var(--v2-warning);
}

.helpv2-item-icon--legal {
    background: var(--v2-text-3);
}

.helpv2-item-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.helpv2-item-title {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-medium);
    color: var(--v2-text-1);
    line-height: 1.3;
}

.helpv2-item-desc {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    line-height: 1.3;
}

.helpv2-item-arrow {
    font-size: 0.72rem;
    color: var(--v2-text-4);
    flex-shrink: 0;
    transition: color var(--v2-transition-fast),
        transform var(--v2-transition-fast);
}

.helpv2-item:hover .helpv2-item-arrow {
    color: var(--v2-primary);
    transform: translateX(2px);
}

/* Footer promo */
.helpv2-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    border-top: 1px solid var(--v2-border-subtle);
    background: var(--v2-surface-1, rgba(15, 23, 42, 0.02));
    color: var(--v2-text-4);
    font-size: 0.7rem;
    text-decoration: none;
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast);
    flex-shrink: 0;
}

.helpv2-footer:hover {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    text-decoration: none;
}

.helpv2-footer strong {
    color: inherit;
    font-weight: 600;
}

.helpv2-footer-heart {
    font-size: 0.6rem;
    color: #e11d48;
    transition: transform var(--v2-transition-base);
}

.helpv2-footer:hover .helpv2-footer-heart {
    transform: scale(1.3);
}

/* Transitions */
.helpv2-fade-enter-active,
.helpv2-fade-leave-active {
    transition: opacity var(--v2-transition-base),
        transform var(--v2-transition-base);
}

.helpv2-fade-enter,
.helpv2-fade-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
}
</style>
